import React, { useState, useEffect, useRef } from "react";
import { getProfileQuestions, deleteProfileQuestion } from "./store/index";
import { useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from "react-router-dom";
import { IoIosAddCircle } from "react-icons/io";
import Select from 'react-select';
import { Button, Input, Row, Col, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import Swal from "sweetalert2";
import { ComponentLoader } from '../common/Loader';
import noRecordImg from '../../../images/empty-folder.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import './ProfileQuestions.css';

const ProfileQuestions = () => {
    const [allData, setAllData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const hasFetched = useRef(false);

    // Filters state
    const [filters, setFilters] = useState({
        title: "",
        version: "",
        inputType: "",
        priority: "",
        startDate: "",
        endDate: "",
        gender: ""
    });

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) {
            return <FontAwesomeIcon icon={faSort} className="ms-1 text-muted" style={{ fontSize: '12px' }} />;
        }
        if (sortConfig.direction === 'asc') {
            return <FontAwesomeIcon icon={faSortUp} className="ms-1 text-dark" style={{ fontSize: '12px' }} />;
        }
        return <FontAwesomeIcon icon={faSortDown} className="ms-1 text-dark" style={{ fontSize: '12px' }} />;
    };

    const fetchData = async () => {
        try {
            setIsLoading(true);
            const payload = {
                ...filters,
                page: currentPage,
                limit: itemsPerPage
            };

            const response = await dispatch(getProfileQuestions(payload));
            if (response.payload && response.payload.data) {
                const crypto = require('../../../services/crypto');
                const decryptedData = crypto.AesDecrypt(response.payload.data);

                setFilteredData(decryptedData.questions || []);
                setTotalRecords(decryptedData.totalRecords || 0);
                setTotalPages(decryptedData.totalPages || 0);
                setCurrentPage(decryptedData.currentPage || 1);
            } else {
                setFilteredData([]);
                setTotalRecords(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error("Error fetching profile questions data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchData();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [filters, currentPage, itemsPerPage]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
        setCurrentPage(1);
    };

    const handleClearFilters = () => {
        setFilters({
            title: "",
            version: "",
            inputType: "",
            priority: "",
            startDate: "",
            endDate: "",
            gender: ""
        });
        setCurrentPage(1);
    };

    const handleEdit = (id) => {
        sessionStorage.removeItem('PROFILE_QUESTIONS_CACHE');
        navigate(`/profile-questions/edit/${id}`);
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Profile Questions',
            text: 'Are you sure want to delete this question?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('Yes, Delete'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteProfileQuestion(id)).then((res) => {
                    if (res.payload && res.payload.response === 'S') {
                        Swal.fire('Deleted!', 'Question deleted successfully.', 'success');
                        fetchData();
                    } else {
                        Swal.fire('Error', 'Failed to delete question.', 'error');
                    }
                });
            }
        });
    };

    // Pagination and Sorting Calculation
    const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
    const indexOfLastItem = Math.min(indexOfFirstItem + itemsPerPage, totalRecords);
    
    let currentItems = [...filteredData];
    if (sortConfig.key) {
        currentItems.sort((a, b) => {
            let aVal, bVal;
            
            if (sortConfig.key === 'gender') {
                aVal = a.targetAudience?.gender?.join(', ') || '';
                bVal = b.targetAudience?.gender?.join(', ') || '';
            } else {
                aVal = a[sortConfig.key];
                bVal = b[sortConfig.key];
            }
            
            // Handle undefined/null
            if (aVal === undefined || aVal === null) aVal = '';
            if (bVal === undefined || bVal === null) bVal = '';

            // Handle string comparisons case-insensitively
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const getGenderBadgeClass = (gender) => {
        switch (gender?.toLowerCase()) {
            case 'male': return 'pq-badge-male';
            case 'female': return 'pq-badge-female';
            case 'all': return 'pq-badge-all';
            default: return 'pq-badge-default';
        }
    };

    const getInputTypeBadgeClass = (type) => {
        switch (type?.toLowerCase()) {
            case 'text': return 'pq-type-text';
            case 'number': return 'pq-type-number';
            case 'single_select': return 'pq-type-single';
            case 'multi_select': return 'pq-type-multi';
            default: return '';
        }
    };

    const formatInputType = (type) => {
        if (!type) return '';
        return type.split(/[_-]/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderRadius: '8px',
            borderColor: '#e1e5eb',
            boxShadow: 'none',
            padding: '0 5px',
            minHeight: '48px',
            height: '48px',
            fontSize: '13px',
            backgroundColor: '#ffffff',
            '&:hover': {
                borderColor: '#8b5cf6'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            fontSize: '13px',
            backgroundColor: state.isSelected ? '#f4f6f9' : 'white',
            color: '#495057',
            '&:hover': {
                backgroundColor: '#f8f9fa'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            fontSize: '12px',
            color: '#495057'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const formatOptionLabel = ({ label, color }) => (
        <div style={{ display: "flex", alignItems: "center" }}>
            {color && <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color, marginRight: 8, display: 'inline-block' }}></span>}
            {label}
        </div>
    );

    const filterInputTypeOptions = [
        { value: "", label: t('All Input Types') },
        { value: "text", label: "Text Input", color: "#6c757d" },
        { value: "number", label: "Number Input", color: "#6c757d" },
        { value: "single_select", label: "Single Select", color: "#15803d" },
        { value: "multi_select", label: "Multi Select", color: "#4338ca" }
    ];

    const filterGenderOptions = [
        { value: "", label: t('Gender') },
        { value: "all", label: "All", color: "#7e22ce" },
        { value: "male", label: "Male", color: "#0284c7" },
        { value: "female", label: "Female", color: "#be185d" },
        { value: "other", label: "Other", color: "#b45309" }
    ];

    const hasActiveFilters = Boolean(
        filters.title || 
        filters.startDate || 
        filters.endDate || 
        (filters.inputType && filters.inputType !== '') || 
        (filters.gender && filters.gender !== '' && filters.gender !== 'all') || 
        filters.version || 
        filters.priority
    );

    return (
        <div className="pq-list-container">
            <div
  className="d-flex justify-content-between align-items-center mb-4"
  style={{
    padding: "0px 2px",
    borderRadius: "12px",
 
  }}
>
  <div>
    <h2
      style={{
        margin: "0",
        color: "#212529",
        fontWeight: "700",
        fontSize: "28px"
      }}
    >
      Profile Questions
    </h2>

    <p
      style={{
        margin: "6px 0 0",
        color: "#6c757d",
        fontSize: "14px"
      }}
    >
      Manage and create profile questions for users.
    </p>
  </div>

  <button
    onClick={() => navigate("/profile-questions/add")}
    style={{
      background: "#0d6efd",
      color: "#fff",
      border: "none",
      borderRadius: "50px",
      padding: "10px 22px",
      fontSize: "15px",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      cursor: "pointer",
      boxShadow: "0 4px 12px rgba(13,110,253,0.3)"
    }}
  >
    <i className="fa fa-plus"></i>
    {t("Add Profile Question")}
  </button>
</div>

            {/* Filter Bar */}
            <div className="pq-filter-container">
                <Row className="g-3 align-items-center">
                    <Col md={2}>
                        <Input type="text" name="title" placeholder={t("Search by title...")} value={filters.title} onChange={handleFilterChange} className="pq-filter-input" />
                    </Col>
                    <Col md={2}>
                        <Input
                            type={filters.startDate ? "date" : "text"}
                            name="startDate"
                            placeholder={t("Start Date")}
                            value={filters.startDate}
                            max={filters.endDate}
                            onChange={handleFilterChange}
                            onFocus={(e) => (e.target.type = "date")}
                            onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
                            className="pq-filter-input"
                        />
                    </Col>
                    <Col md={2}>
                        <Input
                            type={filters.endDate ? "date" : "text"}
                            name="endDate"
                            placeholder={t("End Date")}
                            value={filters.endDate}
                            min={filters.startDate}
                            onChange={handleFilterChange}
                            onFocus={(e) => (e.target.type = "date")}
                            onBlur={(e) => (!e.target.value ? (e.target.type = "text") : null)}
                            className="pq-filter-input"
                        />
                    </Col>
                    <Col md={2}>
                        <Select
                            options={filterInputTypeOptions}
                            value={filterInputTypeOptions.find(o => o.value === filters.inputType) || filterInputTypeOptions[0]}
                            onChange={(option) => handleFilterChange({ target: { name: 'inputType', value: option.value } })}
                            styles={customSelectStyles}
                            formatOptionLabel={formatOptionLabel}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={1}>
                        <Select
                            options={filterGenderOptions}
                            value={filterGenderOptions.find(o => o.value === filters.gender) || filterGenderOptions[0]}
                            onChange={(option) => handleFilterChange({ target: { name: 'gender', value: option.value } })}
                            styles={customSelectStyles}
                            formatOptionLabel={formatOptionLabel}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                        />
                    </Col>
                    <Col md={1}>
                        <Input type="number" name="version" placeholder={t("Ver")} value={filters.version} onChange={handleFilterChange} className="pq-filter-input" />
                    </Col>
                    <Col md={1}>
                        <Input type="number" name="priority" placeholder={t("Pri")} value={filters.priority} onChange={handleFilterChange} className="pq-filter-input" />
                    </Col>
                    {hasActiveFilters && (
                        <Col md={1} className="col-md-1">
                            <button onClick={handleClearFilters} style={{ height: '36px', padding: '0 16px', backgroundColor: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', transition: 'all 0.2s' }} title={t("Clear Filters")}>
                                CLEAR
                            </button>
                        </Col>
                    )}
                </Row>
            </div>

            <div className="pq-table-wrapper">
                <div className="pq-table-scroll">
                    <table className="pq-table">
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'center' }}>{t('S.NO')}</th>
                                <th style={{ cursor: 'pointer' }} onClick={() => handleSort('title')}>
                                    {t('TITLE')} {renderSortIcon('title')}
                                </th>
                                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('inputType')}>
                                    {t('INPUT TYPE')} {renderSortIcon('inputType')}
                                </th>
                                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('priority')}>
                                    {t('PRIORITY')} {renderSortIcon('priority')}
                                </th>
                                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('version')}>
                                    {t('VERSION')} {renderSortIcon('version')}
                                </th>
                                <th style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => handleSort('gender')}>
                                    {t('GENDER')} {renderSortIcon('gender')}
                                </th>
                                <th style={{ textAlign: 'center' }}>{t('AGE')}</th>
                                <th style={{ textAlign: 'center' }}>{t('ACTIONS')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan="8">
                                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '54vh' }}>
                                            <ComponentLoader color="#8b5cf6" size="40px" />
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredData.length === 0 ? (
                                <tr>
                                    <td colSpan="8">
                                        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
                                            <img src={noRecordImg} alt="No Record Found" style={{ height: '150px', marginBottom: '20px' }} />
                                            <h5 style={{ color: '#6e7480ff', fontWeight: 'bold' }}>No Record Found</h5>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'center' }}>{indexOfFirstItem + index + 1}</td>
                                        <td>{item.title}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className={`pq-type-badge ${getInputTypeBadgeClass(item.inputType)}`}>{formatInputType(item.inputType)}</span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>{item.priority}</td>
                                        <td style={{ textAlign: 'center' }}>{item.version}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            {item.targetAudience?.gender?.length > 0 ? (
                                                item.targetAudience.gender.map((g, i) => (
                                                    <span key={i} className={`pq-badge ${getGenderBadgeClass(g)} me-1`}>
                                                        {g}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className="pq-type-badge" style={{ color: '#000' }}>
                                                ({item.targetAudience?.ageMin || 0} - {item.targetAudience?.ageMax || 120})
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex justify-content-center gap-2">
                                                <button className="pq-action-btn" onClick={() => handleEdit(item._id)} title={t('Edit')}>
                                                    <i className="fas fa-pen"></i>
                                                </button>
                                                <button className="pq-action-btn pq-action-btn-danger" onClick={() => handleDelete(item._id)} title={t('Delete')}>
                                                    <i className="fa fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="pq-pagination-container d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <span className="pq-pagination-text me-2">Showing {totalRecords > 0 ? indexOfFirstItem + 1 : 0} – {indexOfLastItem} of {totalRecords} entries. Per page: </span>
                        <Input type="select" value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="pq-filter-input" style={{ width: '70px', display: 'inline-block', height: '32px !important', padding: '0 10px !important' }}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </Input>
                    </div>

                    <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item pq-page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                                &laquo;
                            </button>
                        </li>

                        {[...Array(totalPages)].map((_, i) => {
                            if (i === 0 || i === totalPages - 1 || (i >= currentPage - 2 && i <= currentPage)) {
                                return (
                                    <li className={`page-item pq-page-item ${i + 1 === currentPage ? 'active' : ''}`} key={i}>
                                        <button className="page-link pq-page-link" onClick={() => handlePageChange(i + 1)}>
                                            {i + 1}
                                        </button>
                                    </li>
                                );
                            }
                            if (i === 1 && currentPage > 3) return <li className="page-item pq-page-item disabled" key="start-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            if (i === totalPages - 2 && currentPage < totalPages - 2) return <li className="page-item pq-page-item disabled" key="end-ellipsis"><span className="page-link pq-page-link">...</span></li>;
                            return null;
                        })}

                        <li className={`page-item pq-page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                            <button className="page-link pq-page-link" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
                                &raquo;
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default ProfileQuestions;
